import React from 'react';
import { Button } from '@mantine/core';
import { MediaItem } from '../../types';
import { getAspectRatioDisplay } from '../../utils/aspectRatioDetector';
import AspectRatioSplitButton from './AspectRatioSplitButton';
import styles from './VideoEditor.module.css';
import loaderStyles from '../../assets/loader.module.css';
import { useTranslation } from 'react-i18next';

interface LeftPanelProps {
  // Media Items & Aspect Ratio
  mediaItems: MediaItem[];
  aspectRatio: string;
  isDetectingAspectRatio: boolean;
  aspectRatioResult: any;
  
  // Conversion Mode
  isConversionMode: boolean;
  targetAspectRatio: string;
  onStartConversion: (target: string) => void;
  onCancelConversion: () => void;
  
  // Clone Project
  projectId: string;
  projectTitle: string;
  onCloneSuccess: (newProjectId: string, newProjectTitle: string, clonedTargetRatio: string) => void;
  
  // Auto-Generate
  isProcessing: {
    autoGenerateLibrary: boolean;
  };
  disabledBtnPendingDownload: boolean;
  onAutoGenerateLibraryMedia: () => void;
  
  // Watermark
  includeWatermark: boolean;
  onWatermarkChange: (checked: boolean) => void;
  isFreePlan: boolean; // NEW: Track if user is on free plan
  
  // Audio Ref
  audioRef: React.RefObject<HTMLAudioElement>;
  audioUrl: string | null;
  onAudioTimeUpdate: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
  onLoadedMetadata: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
  
  // SearchParams (for clone)
  searchParams: URLSearchParams;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
  mediaItems,
  aspectRatio,
  isDetectingAspectRatio,
  aspectRatioResult,
  isConversionMode,
  targetAspectRatio,
  onStartConversion,
  onCancelConversion,
  projectId,
  projectTitle,
  onCloneSuccess,
  isProcessing,
  disabledBtnPendingDownload,
  onAutoGenerateLibraryMedia,
  includeWatermark,
  onWatermarkChange,
  isFreePlan,
  audioRef,
  audioUrl,
  onAudioTimeUpdate,
  onLoadedMetadata,
  searchParams,
}) => {
  const { t } = useTranslation('videoEditor');
  
  return (
    <div className={styles.left}>
      {/* Aspect Ratio Display */}
      {mediaItems && mediaItems.length > 0 && (() => {
        // Show loader if:
        // 1. Currently detecting, OR
        // 2. Haven't completed initial detection yet (source is fallback and we have media items)
        const isActuallyDetecting = isDetectingAspectRatio || 
          (!aspectRatioResult || aspectRatioResult?.source === 'fallback');
        
        const displayedRatio = isConversionMode && targetAspectRatio ? targetAspectRatio : aspectRatio;
        const currentDisplayInfo = getAspectRatioDisplay(displayedRatio as '16:9' | '9:16' | '1:1');
        
        return (
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#2a2a2a', 
            borderRadius: '8px',
            border: '1px solid #444'
          }}>
            <div style={{ color: '#b0b0b0', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              {t('videoEditor.leftPanel.outputFormat')}:
            </div>
            
            {isActuallyDetecting ? (
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#888',
                fontSize: '0.9rem'
              }}>
                <span className={loaderStyles["lds-roller-min"]}>
                  <div></div><div></div><div></div><div></div>
                  <div></div><div></div><div></div><div></div>
                </span>
                <span>Detecting aspect ratio...</span>
              </div>
            ) : (
              <div style={{ 
                color: '#10b981', 
                fontSize: '1rem', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>{currentDisplayInfo.emoji}</span>
                <span>{displayedRatio}</span>
                <span style={{ color: '#888', fontSize: '0.8rem' }}>
                  ({currentDisplayInfo.label})
                </span>
              </div>
            )}
            
            {aspectRatioResult && process.env.NODE_ENV === 'development' && !isActuallyDetecting && (
              <div style={{ color: '#666', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                Confidence: {aspectRatioResult.confidence} | Source: {aspectRatioResult.source}
              </div>
            )}
          </div>
        );
      })()}

      {/* Aspect Ratio Conversion Button */}
      {mediaItems && mediaItems.length > 0 && aspectRatio !== '9:16' && (
        <AspectRatioSplitButton
          currentAspectRatio={aspectRatio}
          isConversionMode={isConversionMode}
          onStartConversion={onStartConversion}
          onCancelConversion={onCancelConversion}
          disabled={disabledBtnPendingDownload}
          projectId={projectId}
          projectTitle={projectTitle}
          onCloneSuccess={onCloneSuccess}
        />
      )}
      
      {/* Auto-Generate Media Button */}
      <div className={styles.aiControls}>
        <Button 
          className={styles.aiButton}
          onClick={onAutoGenerateLibraryMedia}
          disabled={disabledBtnPendingDownload}
          style={{ width: '100%', marginBottom: '0.5rem' }}
        >
          {isProcessing.autoGenerateLibrary ? 
            <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span className={loaderStyles["lds-roller-min"]}>
                <div></div><div></div><div></div><div></div>
                <div></div><div></div><div></div><div></div>
              </span>
              {t('videoEditor.leftPanel.generating')}
            </span> :
            `✨ ${t('videoEditor.leftPanel.autoGenerate')}`
          }
        </Button>
        
        {/* Watermark Checkbox - Always visible. Checked and disabled for free users, unchecked and enabled for paid users */}
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #444',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          opacity: isFreePlan ? 0.7 : 1 // Slightly dimmed for free users to show it's disabled
        }}>
          <input
            type="checkbox"
            checked={includeWatermark}
            onChange={(e) => onWatermarkChange(e.target.checked)}
            disabled={isFreePlan} // Disabled for free users
            style={{
              width: '18px',
              height: '18px',
              accentColor: '#8b5cf6',
              cursor: isFreePlan ? 'not-allowed' : 'pointer'
            }}
            id="videoEditorWatermark"
          />
          <label 
            htmlFor="videoEditorWatermark"
            style={{ 
              color: '#e0e0e0', 
              fontSize: '14px',
              fontWeight: '500',
              cursor: isFreePlan ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              width: '100%',
              margin: 0,
              
            }}
          >
            {t('videoEditor.leftPanel.includeWatermark')} {isFreePlan && <span style={{ color: '#888', fontSize: '12px' }}>({t('videoEditor.watermark.requiredForFreePlan')})</span>}
          </label>
        </div>
      </div>

      {/* Hidden Audio Controls */}
      {audioUrl && (
        <div style={{ display: 'none' }}>
          <audio
            id="audio-player"
            ref={audioRef}
            crossOrigin="anonymous"
            preload="metadata"
            onTimeUpdate={onAudioTimeUpdate}
            onLoadedMetadata={onLoadedMetadata}
            onError={(e) => {
              console.warn('Audio loading error:', e);
            }}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
};

export default LeftPanel;
